import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, X, AlertTriangle, Info } from "lucide-react";

import axios from "../../../services/axiosInstance";
import { fetchMasterWarehouses } from "../raking/warehouseSlice";
import { Panel, formatNumber } from "../../components/DashboardKpiCards";

/* ================= helpers ================= */

const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

// "2026-10-15 13:32:04" -> "15 Okt 2026, 13:32" (tanpa konversi zona waktu, data sudah WIB)
const fmtDateTime = (s) => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!m) return String(s);
    return `${Number(m[3])} ${BULAN[Number(m[2]) - 1]} ${m[1]}, ${m[4]}:${m[5]}`;
};

// detik -> "3 detik", "45 mnt", "5 jam 20 mnt", "2 hari 4 jam"
const fmtDuration = (sec) => {
    if (sec === null || sec === undefined) return null;
    const neg = sec < 0;
    const t = Math.abs(Math.round(sec));
    let text;
    if (t < 60) {
        text = `${t} detik`;
    } else if (t < 3600) {
        text = `${Math.floor(t / 60)} mnt`;
    } else if (t < 86400) {
        const j = Math.floor(t / 3600);
        const m = Math.floor((t % 3600) / 60);
        text = m ? `${j} jam ${m} mnt` : `${j} jam`;
    } else {
        const d = Math.floor(t / 86400);
        const j = Math.floor((t % 86400) / 3600);
        text = j ? `${d} hari ${j} jam` : `${d} hari`;
    }
    return neg ? `−${text}` : text;
};

const toISODate = (d) => {
    const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return toISODate(d);
};

// sama dengan AnalyticsController::DIRECT_FINAL_SECONDS
const DIRECT_FINAL_SECONDS = 60;
const DIRECT_FINAL_HINT =
    "Waktu mulai dan selesai paleting (created_at dan updated_at) sama atau hampir sama. " +
    "Kemungkinan box sedikit dan paleting langsung disimpan final (status 1) tanpa disimpan sebagai draf dulu.";

const STAGES = [
    { key: "paleting", label: "Paleting" },
    { key: "bbm", label: "BBM" },
    { key: "inbound", label: "Inbound" },
    { key: "outbound", label: "Outbound" }
];

const GAPS = [
    { key: "paleting_bbm", from: "Paleting", to: "BBM" },
    { key: "bbm_inbound", from: "BBM", to: "Inbound" },
    { key: "inbound_outbound", from: "Inbound", to: "Outbound" }
];

const STAGE_FILTERS = [
    { value: "", label: "Semua" },
    { value: "paleting", label: "Masih paleting" },
    { value: "bbm", label: "Sudah BBM" },
    { value: "inbound", label: "Di rak" },
    { value: "outbound", label: "Sudah keluar" }
];

const PRESETS = [
    { days: 7, label: "7 hari" },
    { days: 30, label: "30 hari" },
    { days: 90, label: "90 hari" }
];

/* ================= ringkasan alur ================= */

function FlowSummary({ summary, loading }) {
    const stages = summary?.stages || {};
    const durations = summary?.durations || {};
    const total = summary?.total_batch || 0;

    return (
        <Panel className="p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-base font-semibold text-body">Waktu penyelesaian antar proses</h3>
                <p className="text-xs text-muted">
                    Hanya batch paleting berstatus final (bukan draf). Angka utama median, rata-rata di bawahnya.
                </p>
            </div>

            <div className={`mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-stretch gap-3 ${loading ? "opacity-50" : ""}`}>
                {STAGES.map((s, i) => {
                    const count = stages[s.key] || 0;
                    const pct = total ? Math.round((count / total) * 100) : 0;
                    const gap = GAPS[i];
                    const d = gap ? durations[gap.key] : null;

                    return [
                        <div key={s.key} className="rounded-lg border border-line bg-surface/60 px-4 py-3">
                            <div className="text-sm text-muted">{s.label}</div>
                            <div className="text-2xl font-semibold text-body tabular-nums mt-1">
                                {formatNumber(count)}
                            </div>
                            <div className="text-xs text-muted mt-0.5">
                                batch{i > 0 && total ? `, ${pct}% dari paleting` : ""}
                            </div>
                            {s.key === "paleting" && (
                                <div className="text-xs text-muted mt-2 pt-2 border-t border-line">
                                    Lama paleting{" "}
                                    <span className="font-semibold text-ink">
                                        {fmtDuration(durations?.paleting?.median_seconds) || "—"}
                                    </span>
                                    <span className="block text-[11px]">
                                        rata-rata {fmtDuration(durations?.paleting?.avg_seconds) || "—"}
                                    </span>
                                    {summary?.paleting_direct_final > 0 && (
                                        <span className="block text-[11px] mt-1" title={DIRECT_FINAL_HINT}>
                                            {formatNumber(summary.paleting_direct_final)} batch langsung final tanpa draf
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>,

                        gap && (
                            <div
                                key={`${s.key}-gap`}
                                className="flex lg:flex-col items-center justify-center gap-2 lg:gap-1 px-1 text-center"
                            >
                                <div className="hidden lg:block w-full h-px bg-ink/40 relative">
                                    <span className="absolute -right-1 -top-[3px] w-0 h-0 border-y-[4px] border-y-transparent border-l-[6px] border-l-ink/60" />
                                </div>
                                <div className="text-base font-semibold text-ink tabular-nums whitespace-nowrap">
                                    {fmtDuration(d?.median_seconds) || "—"}
                                </div>
                                <div className="text-[11px] text-muted whitespace-nowrap">
                                    rata-rata {fmtDuration(d?.avg_seconds) || "—"}
                                </div>
                            </div>
                        )
                    ];
                })}
            </div>

            <div className="mt-4 pt-4 border-t border-line grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                    {
                        key: "to_inbound",
                        label: "Paleting sampai masuk rak",
                        hint: "Dari mulai paleting sampai palet pertama masuk rak. Dihitung dari semua batch yang sudah inbound."
                    },
                    {
                        key: "to_outbound",
                        label: "Paleting sampai keluar rak",
                        hint: "Dari mulai paleting sampai pertama kali keluar rak. Hanya batch yang sudah outbound."
                    }
                ].map((t) => {
                    const v = durations?.[t.key];
                    return (
                        <div key={t.key} className="flex items-baseline justify-between gap-4 rounded-lg bg-surface/60 px-4 py-3" title={t.hint}>
                            <div className="min-w-0">
                                <div className="text-sm text-body font-medium">{t.label}</div>
                                <div className="text-xs text-muted mt-0.5">
                                    {v?.count ? `${formatNumber(v.count)} batch` : "belum ada batch"}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-lg font-semibold text-ink tabular-nums">
                                    {fmtDuration(v?.median_seconds) || "—"}
                                </div>
                                <div className="text-[11px] text-muted">
                                    rata-rata {fmtDuration(v?.avg_seconds) || "—"}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Panel>
    );
}

/* ================= baris timeline per batch ================= */

function StepDot({ done }) {
    return (
        <span
            className={`relative z-10 w-3 h-3 rounded-full shrink-0 ${
                done ? "bg-ink ring-4 ring-ink-100" : "bg-white border-2 border-line"
            }`}
        />
    );
}

function GapLabel({ seconds }) {
    if (seconds === null || seconds === undefined) return null;
    if (seconds < 0) {
        return (
            <span
                className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700"
                title="Waktu tahap ini lebih awal dari tahap sebelumnya. Periksa data transaksi."
            >
                <AlertTriangle size={12} /> tidak urut
            </span>
        );
    }
    return (
        <span className="text-[11px] font-semibold text-ink bg-ink-100 rounded px-1.5 py-0.5 tabular-nums whitespace-nowrap">
            {fmtDuration(seconds)}
        </span>
    );
}

function BatchTimeline({ row }) {
    const d = row.durations || {};

    const steps = [
        {
            label: "Paleting",
            at: row.paleting_at,
            doneAt: row.paleting_done_at,
            duration: d.paleting,
            note: row.pal_boxes ? `${formatNumber(row.pal_boxes)} box, qty ${formatNumber(row.pal_qty)}` : null
        },
        {
            label: "BBM",
            at: row.bbm_at,
            note: row.bbm_items ? `${formatNumber(row.bbm_items)} item, qty ${formatNumber(row.bbm_qty)}` : null
        },
        {
            label: "Inbound",
            at: row.in_first,
            note: row.in_first ? `${formatNumber(row.in_moved)} dari ${formatNumber(row.bbm_items)} masuk rak` : null
        },
        {
            label: "Outbound",
            at: row.out_first,
            note: row.out_first ? `qty keluar ${formatNumber(row.out_qty)}` : null
        }
    ];
    const gaps = [d.paleting_bbm, d.bbm_inbound, d.inbound_outbound];

    return (
        <li className="px-5 py-4 grid grid-cols-1 xl:grid-cols-[minmax(260px,320px)_1fr] gap-4 xl:gap-8">

            {/* identitas batch */}
            <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-base font-semibold text-body tabular-nums">{row.batch_id}</span>
                    {row.batch_number && (
                        <span className="text-xs text-muted tabular-nums">no. {row.batch_number}</span>
                    )}
                </div>
                <div className="text-sm text-body mt-0.5 truncate" title={row.description}>
                    {row.description || "-"}
                </div>
                <div className="text-xs text-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="tabular-nums">{row.item_code}</span>
                    <span>Lot {row.lot_number || "-"}</span>
                    <span>{row.warehouse_name || "-"}</span>
                </div>
                {d.total !== null && d.total !== undefined && d.total >= 0 && (
                    <div className="text-xs text-muted mt-2">
                        Total <span className="font-semibold text-body">{fmtDuration(d.total)}</span>
                    </div>
                )}
            </div>

            {/* timeline: horizontal di layar lebar, vertikal di HP */}
            <ol className="grid grid-cols-1 md:grid-cols-4 gap-y-3">
                {steps.map((s, i) => {
                    const done = Boolean(s.at);
                    const nextDone = Boolean(steps[i + 1]?.at);
                    return (
                        <li key={s.label} className="relative flex md:block gap-3 md:pr-3">
                            {/* garis penghubung */}
                            {i < steps.length - 1 && (
                                <>
                                    <span
                                        className={`hidden md:block absolute top-[5px] left-3 right-0 h-0.5 ${
                                            nextDone ? "bg-ink" : "bg-line"
                                        }`}
                                    />
                                    <span
                                        className={`md:hidden absolute left-[5px] top-4 -bottom-3 w-0.5 ${
                                            nextDone ? "bg-ink" : "bg-line"
                                        }`}
                                    />
                                </>
                            )}

                            <div className="flex md:items-center gap-2">
                                <StepDot done={done} />
                                {i < steps.length - 1 && (
                                    <span className="hidden md:inline-block relative z-10 ml-auto mr-2 bg-white px-1">
                                        <GapLabel seconds={gaps[i]} />
                                    </span>
                                )}
                            </div>

                            <div className="md:mt-2 min-w-0">
                                <div className={`text-sm font-semibold ${done ? "text-body" : "text-muted"}`}>
                                    {s.label}
                                    {i > 0 && (
                                        <span className="md:hidden ml-2 align-middle">
                                            <GapLabel seconds={gaps[i - 1]} />
                                        </span>
                                    )}
                                </div>
                                <div className={`text-xs tabular-nums ${done ? "text-body" : "text-muted"}`}>
                                    {fmtDateTime(s.at) || "Belum"}
                                </div>
                                {s.doneAt && (
                                    <div className="text-xs tabular-nums text-body">
                                        <span className="text-muted">s/d</span> {fmtDateTime(s.doneAt)}
                                    </div>
                                )}
                                {s.duration !== undefined && s.duration !== null && s.duration >= 0 && (
                                    <div className="text-[11px] text-muted mt-0.5">
                                        lama paleting <span className="font-semibold text-body">{fmtDuration(s.duration)}</span>
                                    </div>
                                )}
                                {s.duration !== undefined && s.duration !== null && s.duration >= 0 && s.duration < DIRECT_FINAL_SECONDS && (
                                    <div
                                        className="inline-flex items-center gap-1 mt-1 text-[11px] font-medium text-ink bg-ink-100 rounded px-1.5 py-0.5"
                                        title={DIRECT_FINAL_HINT}
                                    >
                                        <Info size={12} className="shrink-0" />
                                        Langsung final tanpa draf
                                    </div>
                                )}
                                {s.note && <div className="text-[11px] text-muted mt-0.5">{s.note}</div>}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </li>
    );
}

/* ================= halaman ================= */

export default function AnalyticsPage() {
    const dispatch = useDispatch();
    const warehouses = useSelector((state) => state.masterWarehouses?.data) || [];

    const [warehouseId, setWarehouseId] = useState("");
    const [dateFrom, setDateFrom] = useState(daysAgo(29));
    const [dateTo, setDateTo] = useState(toISODate(new Date()));
    const [search, setSearch] = useState("");
    const [stage, setStage] = useState("");
    const [page, setPage] = useState(1);

    const [data, setData] = useState({ summary: null, data: [], pagination: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const requestId = useRef(0);

    useEffect(() => {
        if (!warehouses.length) dispatch(fetchMasterWarehouses());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // kembali ke halaman 1 setiap filter berubah
    useEffect(() => {
        setPage(1);
    }, [warehouseId, dateFrom, dateTo, search, stage]);

    useEffect(() => {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);

        const timer = setTimeout(() => {
            axios
                .get("/analytics/timeline", {
                    params: {
                        warehouse_id: warehouseId || undefined,
                        date_from: dateFrom || undefined,
                        date_to: dateTo || undefined,
                        search: search.trim() || undefined,
                        stage: stage || undefined,
                        page,
                        per_page: 20
                    }
                })
                .then((res) => {
                    if (id !== requestId.current) return;
                    setData({
                        summary: res.data?.summary || null,
                        data: Array.isArray(res.data?.data) ? res.data.data : [],
                        pagination: res.data?.pagination || null
                    });
                })
                .catch((err) => {
                    if (id !== requestId.current) return;
                    setError(
                        err.response?.data?.message ||
                        "Data timeline tidak bisa diambil. Periksa koneksi ke server lalu coba lagi."
                    );
                })
                .finally(() => {
                    if (id === requestId.current) setLoading(false);
                });
        }, search ? 400 : 0);

        return () => clearTimeout(timer);
    }, [warehouseId, dateFrom, dateTo, search, stage, page]);

    const pg = data.pagination || { page: 1, last_page: 1, total: 0, per_page: 20 };
    const currentStage = data.summary?.current_stage || {};

    const activePreset = useMemo(() => {
        if (dateTo !== toISODate(new Date())) return null;
        return PRESETS.find((p) => dateFrom === daysAgo(p.days - 1))?.days || null;
    }, [dateFrom, dateTo]);

    const inputClass = "h-10 bg-white border border-line rounded-md px-3 text-sm text-body focus:border-ink focus:ring-0";

    return (
        <div className="max-w-[1600px] mx-auto space-y-5">

            {/* ===== JUDUL + FILTER ===== */}
            <div className="space-y-4">
                <div>
                    <h2 className="text-2xl font-semibold text-body tracking-tight">
                        Timeline proses batch
                    </h2>
                    <p className="text-sm text-muted mt-1">
                        Audit trail dari paleting, BBM, masuk rak, sampai keluar rak, beserta lama tiap proses
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
                    <label className="flex flex-col gap-1">
                        <span className="text-xs text-muted">Warehouse</span>
                        <select
                            value={warehouseId}
                            onChange={(e) => setWarehouseId(e.target.value)}
                            className={`${inputClass} min-w-[200px]`}
                        >
                            <option value="">Semua warehouse</option>
                            {warehouses.map((w) => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </label>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted">Tanggal paleting</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={dateFrom}
                                max={dateTo}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className={inputClass}
                                aria-label="Dari tanggal"
                            />
                            <span className="text-muted text-sm">s/d</span>
                            <input
                                type="date"
                                value={dateTo}
                                min={dateFrom}
                                onChange={(e) => setDateTo(e.target.value)}
                                className={inputClass}
                                aria-label="Sampai tanggal"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted">Cepat</span>
                        <div className="h-10 inline-flex bg-white border border-line rounded-md p-1">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.days}
                                    type="button"
                                    onClick={() => {
                                        setDateFrom(daysAgo(p.days - 1));
                                        setDateTo(toISODate(new Date()));
                                    }}
                                    className={`px-3 text-sm rounded transition-colors ${
                                        activePreset === p.days
                                            ? "bg-ink text-white font-semibold"
                                            : "text-muted hover:text-body"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== RINGKASAN ===== */}
            {error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg px-5 py-4 text-sm text-red-700">{error}</div>
            ) : (
                <FlowSummary summary={data.summary} loading={loading} />
            )}

            {/* ===== DAFTAR ===== */}
            <Panel>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-5 py-4 border-b border-line">
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Tahap terakhir">
                        {STAGE_FILTERS.map((f) => {
                            const active = stage === f.value;
                            const count = f.value ? currentStage[f.value] : data.summary?.total_batch;
                            return (
                                <button
                                    key={f.value || "all"}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    onClick={() => setStage(f.value)}
                                    className={`h-8 px-3 rounded-full text-sm border transition-colors ${
                                        active
                                            ? "bg-ink border-ink text-white font-semibold"
                                            : "bg-white border-line text-body hover:border-ink"
                                    }`}
                                >
                                    {f.label}
                                    {count !== undefined && (
                                        <span className={`ml-1.5 tabular-nums ${active ? "text-white/80" : "text-muted"}`}>
                                            {formatNumber(count)}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <div className="relative w-full lg:w-80">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                        <input
                            type="text"
                            inputMode="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cari batch, lot, kode item, atau nama item"
                            className="h-9 w-full pl-9 pr-9 text-sm border border-line rounded-md focus:border-ink focus:ring-0"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                aria-label="Hapus pencarian"
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-muted hover:text-body hover:bg-surface"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {data.data.length > 0 ? (
                    <ul className={`divide-y divide-line transition-opacity ${loading ? "opacity-50" : ""}`}>
                        {data.data.map((row) => (
                            <BatchTimeline key={row.id} row={row} />
                        ))}
                    </ul>
                ) : (
                    <div className="px-5 py-14 text-center text-sm text-muted">
                        {loading
                            ? "Memuat timeline…"
                            : search
                                ? `Tidak ada batch yang cocok dengan "${search}" di rentang tanggal ini.`
                                : "Belum ada batch yang dipaletkan di rentang tanggal ini. Coba perlebar rentang tanggalnya."}
                    </div>
                )}

                {/* pagination */}
                {pg.total > 0 && (
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line text-sm">
                        <span className="text-muted">
                            {formatNumber((pg.page - 1) * pg.per_page + 1)}–{formatNumber(Math.min(pg.page * pg.per_page, pg.total))} dari {formatNumber(pg.total)} batch
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-muted mr-1 hidden sm:inline">Halaman {pg.page} dari {pg.last_page}</span>
                            <button
                                type="button"
                                disabled={pg.page <= 1 || loading}
                                onClick={() => setPage(pg.page - 1)}
                                className="h-8 px-3 border border-line rounded-md hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                                Sebelumnya
                            </button>
                            <button
                                type="button"
                                disabled={pg.page >= pg.last_page || loading}
                                onClick={() => setPage(pg.page + 1)}
                                className="h-8 px-3 border border-line rounded-md hover:bg-surface disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                                Berikutnya
                            </button>
                        </div>
                    </div>
                )}
            </Panel>
        </div>
    );
}
